import * as React from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

/* ── Variants ─────────────────────────────────────────────── */

const sheetOverlayVariants = cva([
  "fixed inset-0 z-50 bg-coval-overlay",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
])

// A full-height panel pinned to one edge of the viewport, over the page rather
// than beside it: it dims the page, traps focus, locks scroll, and closes on an
// outside click or Escape.
//
// Drawer is the other panel in the set and answers the opposite need — it docks
// alongside the content and keeps it live, so the two are never
// interchangeable. Reach for a sheet where there is no room to sit beside
// anything, as on a narrow viewport, or where the panel is a detour the page
// should wait on.
//
// The width is a default, not a bound: it is a plain `w-*` so a caller's own
// width class replaces it, while the viewport cap keeps the scrim reachable
// beside the panel on the narrowest screens.
const sheetContentVariants = cva(
  [
    "fixed inset-y-0 z-50 flex h-full w-80 max-w-[85vw] flex-col",
    "bg-surface-contrast text-coval-text shadow-lg",
  ],
  {
    variants: {
      side: {
        left: [
          "left-0 border-r border-coval-border",
          "data-[state=open]:animate-in data-[state=open]:slide-in-from-left",
          "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left",
        ],
        right: [
          "right-0 border-l border-coval-border",
          "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
          "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
        ],
      },
    },
    defaultVariants: {
      side: "left",
    },
  },
)

/* ── Root ──────────────────────────────────────────────────── */

const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetClose = DialogPrimitive.Close

/* ── Overlay ──────────────────────────────────────────────── */

const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(sheetOverlayVariants(), className)}
    {...props}
  />
))
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName

/* ── Content ──────────────────────────────────────────────── */

export interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetContentVariants> {
  /**
   * Extra classes for the dimming overlay. The overlay is rendered here rather
   * than by the caller, so raising the content out of the default layer needs
   * this to bring the dimming with it.
   */
  overlayClassName?: string
}

const SheetContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, overlayClassName, side, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <SheetOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(sheetContentVariants({ side }), className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
))
SheetContent.displayName = DialogPrimitive.Content.displayName

/* ── Header ───────────────────────────────────────────────── */

const SheetHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 px-4 pt-4 pb-0", className)}
    {...props}
  />
))
SheetHeader.displayName = "SheetHeader"

/* ── Body ─────────────────────────────────────────────────── */

const SheetBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3",
      className,
    )}
    {...props}
  />
))
SheetBody.displayName = "SheetBody"

/* ── Title ────────────────────────────────────────────────── */

const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-sm font-semibold text-coval-text", className)}
    {...props}
  />
))
SheetTitle.displayName = DialogPrimitive.Title.displayName

/* ── Description ──────────────────────────────────────────── */

const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-coval-text-muted", className)}
    {...props}
  />
))
SheetDescription.displayName = DialogPrimitive.Description.displayName

/* ── Exports ──────────────────────────────────────────────── */

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetTitle,
  SheetDescription,
  sheetOverlayVariants,
  sheetContentVariants,
}
